/**
 * Language switcher — sets and persists the UI-language *preference*. Full
 * message translation activates once an i18n provider is wired; until then this
 * stores the choice (localStorage) so the preference survives reloads and is
 * ready for the translation layer.
 */
import * as React from "react";
import { Check, Globe } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@authe/ui-core";
import { LANGUAGES, LANGUAGE_STORAGE_KEY } from "@/config";
import { Hint } from "./Hint";

function readStored(): string {
  if (typeof window === "undefined") return "en";
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? "en";
  } catch {
    return "en";
  }
}

export function LanguageMenu(): React.ReactElement {
  const [lang, setLang] = React.useState<string>(readStored);

  const select = (code: string) => {
    setLang(code);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
      document.documentElement.lang = code;
    } catch {
      /* storage unavailable — preference stays in-memory for this session */
    }
  };

  const current = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  return (
    <DropdownMenu>
      <Hint label={`Language: ${current?.label ?? "English"}`}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Language">
            <Globe className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>Language</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} onSelect={() => select(l.code)}>
            <span aria-hidden>{l.flag}</span>
            <span className="flex-1">{l.label}</span>
            {l.code === current?.code && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
